/* eslint-disable no-param-reassign */
import * as Sentry from '@sentry/nextjs';
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import axios, { HttpStatusCode } from 'axios';
import Router from 'next/router';
import { ServiceBuilder } from 'ts-retrofit3';

import type { IAuthToken } from '@/Module/Auth/Model/ApiModel';
import { API_BASE, LINKS, SERVICE_GLOBAL_ERROR } from '@/Module/Core/Constant';
import { fetchNewAccessToken, isBrowser } from '@/Module/Core/Helper';
import { httpServiceLoggerInit } from '@/Module/Core/Helper/httpServiceLoggerInit';
import type { IGatewayResponse } from '@/Module/Core/Model/ApiModel';
import CookieService from '@/Module/Core/Service/CookieService';

const axiosInstance: AxiosInstance = axios.create({});

const errorCodes: HttpStatusCode[] = [
  HttpStatusCode.BadGateway,
  HttpStatusCode.Forbidden,
  HttpStatusCode.NotFound,
  HttpStatusCode.Unauthorized,
  HttpStatusCode.InternalServerError,
  HttpStatusCode.TooManyRequests,
];

let isRefreshing = false;
let failedQueue: Array<{
  // eslint-disable-next-line unused-imports/no-unused-vars
  resolve: (value?: unknown) => void;
  // eslint-disable-next-line unused-imports/no-unused-vars
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (config: AxiosResponse) => {
    const data = config.data as IGatewayResponse;

    if ((config as any)?.config?.extraMap?.disableResponseAutoError) return config;
    if (
      (!data.isSuccess || errorCodes.includes(data.responseType)) &&
      !config?.config?.url?.includes('wp-json') &&
      !config?.config?.url?.includes('hamrahi.cloud') &&
      !config?.config?.url?.includes('vodcore.iranlms.ir')
    ) {
      config.status = data.responseType;
      return Promise.reject(config);
    }

    return config;
  },
  failedRequest => Promise.reject(failedRequest),
);

axiosInstance.interceptors.response.use(
  (config: AxiosResponse) => config,
  async failedRequest => {
    const { response, config, code, status } = failedRequest;
    const originalRequest = config;

    // eslint-disable-next-line no-underscore-dangle
    if (isBrowser() && (status === 401 || response?.status === 401) && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      // eslint-disable-next-line no-underscore-dangle
      originalRequest._retry = true;
      isRefreshing = true;

      const theRefreshToken = CookieService.getRefreshToken();
      const shadToken = CookieService.getShadToken();

      if (!theRefreshToken) {
        processQueue(null, null);
        if (shadToken) {
          Router.push(LINKS.AUTH.SHAD_LOGOUT);
        } else {
          Router.push({
            pathname: LINKS.AUTH.MULTI_DEVICE_LOGOUT,
            query: { result: 'no_refresh_token' },
          });
        }
        return Promise.reject(failedRequest);
      }

      const oldRefreshToken = decodeURIComponent(theRefreshToken);

      if (oldRefreshToken) {
        return fetchNewAccessToken(oldRefreshToken)
          .then(newAuthToken => {
            const { access_token: accessToken, refresh_token: refreshToken } = newAuthToken as IAuthToken;
            if (!accessToken) {
              processQueue(null, null);
              if (shadToken) {
                Router.push(LINKS.AUTH.SHAD_LOGOUT);
              } else {
                Router.push({
                  pathname: LINKS.AUTH.MULTI_DEVICE_LOGOUT,
                  query: { result: newAuthToken.toString() },
                });
              }
              return Promise.reject(failedRequest);
            }

            CookieService.setAccessToken(accessToken);
            CookieService.setRefreshToken(refreshToken);

            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            processQueue(null, accessToken);
            isRefreshing = false;

            return axiosInstance(originalRequest);
          })
          .catch(refreshError => {
            processQueue(refreshError, null);
            // TODO: Commented code to check if it is needed or not.
            // if (shadToken) {
            //   Router.push(LINKS.AUTH.SHAD_LOGOUT);
            // } else {
            //   Router.push({
            //     pathname: LINKS.AUTH.MULTI_DEVICE_LOGOUT,
            //     query: { result: (refreshError as AxiosError).response?.status },
            //   });
            // }
            return Promise.reject(refreshError);
          });
      }

      processQueue(null, null);
      // TODO: Commented code to check if it is needed or not.
      // if (shadToken) {
      //   Router.push(LINKS.AUTH.SHAD_LOGOUT);
      // } else {
      //   Router.push({
      //     pathname: LINKS.AUTH.MULTI_DEVICE_LOGOUT,
      //     query: { result: 'something_went_wrong' },
      //   });
      // }

      return Promise.reject(failedRequest);
    }

    if (
      isBrowser() &&
      status !== 401 &&
      response?.status !== 401 &&
      status !== 404 &&
      response?.status !== 404 &&
      status !== 500 &&
      response?.status !== 500 &&
      !config?.extraMap?.disabledAutoError &&
      !config?.extraMap?.disableResponseAutoError &&
      code !== 'ERR_CANCELED'
    ) {
      const message = response?.data?.errors?.[0] || SERVICE_GLOBAL_ERROR;
      Sentry.captureEvent({
        message,
        level: 'error',
        logger: 'HttpService',
      });
    }

    return Promise.reject(failedRequest);
  },
);

axiosInstance.interceptors.request.use(
  config => {
    // for clientside
    // @ts-ignore
    if (isBrowser() && !config?.extraMap?.disableToken) {
      const token = CookieService.getAccessToken();
      // @ts-ignore
      if (token && !config?.guestMode) config.headers.Authorization = `Bearer ${token}`;
    }

    // for ssr
    if (config.headers?.serverAuthorization) {
      config.headers.Authorization = `Bearer ${config.headers.serverAuthorization}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

httpServiceLoggerInit(axiosInstance);

export default new ServiceBuilder().setEndpoint(API_BASE).setStandalone(axiosInstance as any);
